// KidGuard Dashboard main entry point
import { onAuthStateChange, logout, getCurrentUser } from './auth/index';
import { db } from './firebase/db';
import { appStore } from './state/store';
import { show, hide, query, queryAll } from './util/dom';
import { createHealthPanel, HEALTH_PANEL_STYLES, calculateDeviceHealth, getDesiredState, getAppliedState } from './devices/index';
import { useSchedules, useDayOverrides, createSchedulesList, createScheduleEditor, createDayOverrideEditor } from './schedules/index';
import type { ScheduleWindow, DayLimitOverride } from './schedules/index';
import { createUsageDashboard } from './usage/index';
import type { UsageDashboardProps } from './usage/index';
import { createSettingsView } from './settings/index';
import type { SettingsViewProps } from './settings/index';
import { createNetworkDashboard } from './network/index';
import type { NetworkDashboardProps } from './network/index';

// Initialize app
async function init() {
  console.log('Initializing KidGuard Dashboard...');

  // Inject health panel styles
  const style = document.createElement('style');
  style.textContent = HEALTH_PANEL_STYLES;
  document.head.appendChild(style);

  // Set up auth state listener
  onAuthStateChange(async (user) => {
    if (user) {
      appStore.setState(prev => ({ ...prev, user }));
      console.log('User signed in:', user.email);
      initializeAppShell();
    } else {
      appStore.setState(prev => ({ ...prev, user: null }));
      console.log('User signed out');
      showSignedOutView();
    }
  });
}

function initializeAppShell() {
  const signedOutView = query('#signedOutView');
  const appShell = query('#appShell');

  if (signedOutView) hide(signedOutView);
  if (appShell) show(appShell);

  console.log('App shell initialized');

  // Set up device detail health panel observer
  setupDeviceDetailObserver();
}

function setupDeviceDetailObserver() {
  // This will be called when a device is selected in the dashboard
  // For now, just log that we're ready
  console.log('Device detail observer ready');
}

function showSignedOutView() {
  const signedOutView = query('#signedOutView');
  const appShell = query('#appShell');

  if (signedOutView) show(signedOutView);
  if (appShell) hide(appShell);

  // Set up sign out button if it exists
  const signOutBtn = query('#signOutBtn');
  if (signOutBtn) {
    (signOutBtn as HTMLButtonElement).onclick = async () => {
      try {
        await logout();
      } catch (err) {
        console.error('Sign out failed:', err);
      }
    };
  }
}

// Expose health panel rendering for dashboard integration
(window as any).renderDeviceHealthPanel = async (deviceElement: HTMLElement, device: any, familyId: string) => {
  try {
    const desired = await getDesiredState(familyId, device.id);
    const applied = await getAppliedState(familyId, device.id);
    const health = calculateDeviceHealth(device, desired, applied);

    const panel = createHealthPanel(health);
    const detailSection = query('.device-detail-section', deviceElement);
    if (detailSection) {
      detailSection.insertBefore(panel, detailSection.firstChild);
    }
  } catch (err) {
    console.error('Failed to render health panel:', err);
  }
};

// Expose schedules rendering for dashboard integration
(window as any).renderSchedulesList = async (containerElement: HTMLElement, familyId: string, deviceId: string) => {
  try {
    const schedulesHook = useSchedules(familyId, deviceId);
    const overridesHook = useDayOverrides(familyId, deviceId);

    let currentScheduleEditor: HTMLElement | null = null;
    let currentOverrideEditor: HTMLElement | null = null;

    const updateList = () => {
      const container = containerElement.querySelector('.schedules-list-container');
      if (container) {
        container.remove();
      }

      const listComponent = createSchedulesList({
        schedules: schedulesHook.schedules,
        dayOverrides: overridesHook.overrides,
        loading: schedulesHook.loading || overridesHook.loading,
        onEditSchedule: (schedule) => openScheduleEditor(schedule),
        onDeleteSchedule: async (id) => {
          await schedulesHook.deleteSchedule(id);
        },
        onAddSchedule: () => openScheduleEditor(undefined),
        onEditOverride: (override) => openOverrideEditor(override),
        onDeleteOverride: async (id) => {
          await overridesHook.deleteOverride(id);
        },
        onAddOverride: () => openOverrideEditor(undefined),
      });

      containerElement.appendChild(listComponent);
    };

    const openScheduleEditor = (schedule: ScheduleWindow | undefined) => {
      if (currentScheduleEditor) {
        currentScheduleEditor.remove();
      }

      currentScheduleEditor = createScheduleEditor({
        schedule,
        onSave: async (newSchedule) => {
          if (schedule) {
            await schedulesHook.updateSchedule({ ...newSchedule, id: schedule.id });
          } else {
            await schedulesHook.addSchedule(newSchedule);
          }
          currentScheduleEditor?.remove();
          currentScheduleEditor = null;
        },
        onCancel: () => {
          currentScheduleEditor?.remove();
          currentScheduleEditor = null;
        },
      });

      const modal = document.createElement('div');
      modal.className = 'schedules-modal-backdrop';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      modal.appendChild(currentScheduleEditor);
      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
          currentScheduleEditor = null;
        }
      });
    };

    const openOverrideEditor = (override: DayLimitOverride | undefined) => {
      if (currentOverrideEditor) {
        currentOverrideEditor.remove();
      }

      currentOverrideEditor = createDayOverrideEditor({
        override,
        onSave: async (newOverride) => {
          if (override) {
            await overridesHook.updateOverride({ ...newOverride, id: override.id });
          } else {
            await overridesHook.addOverride(newOverride);
          }
          currentOverrideEditor?.remove();
          currentOverrideEditor = null;
        },
        onCancel: () => {
          currentOverrideEditor?.remove();
          currentOverrideEditor = null;
        },
      });

      const modal = document.createElement('div');
      modal.className = 'schedules-modal-backdrop';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      modal.appendChild(currentOverrideEditor);
      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
          currentOverrideEditor = null;
        }
      });
    };

    // Initial render
    updateList();

    // Return cleanup function
    return () => {
      schedulesHook.unsubscribe();
      overridesHook.unsubscribe();
    };
  } catch (err) {
    console.error('Failed to render schedules list:', err);
  }
};

// Expose usage dashboard rendering for dashboard integration
(window as any).renderUsageDashboard = async (containerElement: HTMLElement, familyId: string, deviceId: string, dailyLimit?: number) => {
  try {
    const dashboard = await createUsageDashboard({
      familyId,
      deviceId,
      dailyLimit,
      onError: (error) => console.error('Usage dashboard error:', error),
    });
    containerElement.appendChild(dashboard);
  } catch (err) {
    console.error('Failed to render usage dashboard:', err);
  }
};

// Expose settings view rendering for dashboard integration
(window as any).renderSettingsView = async (containerElement: HTMLElement, familyId: string, deviceId: string) => {
  try {
    const view = await createSettingsView({
      familyId,
      deviceId,
      onError: (error) => console.error('Settings error:', error),
      onSuccess: (message) => console.log('Settings success:', message),
    });
    containerElement.appendChild(view);
  } catch (err) {
    console.error('Failed to render settings view:', err);
  }
};

// Expose network dashboard rendering for dashboard integration
(window as any).renderNetworkDashboard = async (containerElement: HTMLElement, familyId: string, deviceId: string) => {
  try {
    const dashboard = await createNetworkDashboard({
      familyId,
      deviceId,
      onError: (error) => console.error('Network dashboard error:', error),
      onSuccess: (message) => console.log('Network dashboard success:', message),
    });
    containerElement.appendChild(dashboard);
  } catch (err) {
    console.error('Failed to render network dashboard:', err);
  }
};

// Start the app
document.addEventListener('DOMContentLoaded', init);
